// ============= Imports =============

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { involvementPermissions } from '../../../../../apis/permissions/get/involvementPermissions';
import { UseAuth } from '../../../../../contexts/AuthContext';
import { useFormContext } from '../../../../../contexts/useFormContext';
import { createLogger } from '../../../../../utils/logger';
import {
  canEditAnyWorkflow,
  canEditOwnWorkflows,
  isManager,
  isUserResponsibleLawyer,
} from '../../../../../utils/permissions';
import {
  DEFAULT_PERMISSION_DENIED_MESSAGE,
  UNAUTHORISED_SECTION_MESSAGE,
} from './constants';
import type {
  UseFormSectionPermissionsParams,
  UseFormSectionPermissionsReturn,
} from './types';
import {
  isOvertimeApprovalSectionKey,
  withTemporaryOvertimeAssignedSections,
} from '../../schemas/OvertimeApproval/utils';
import { DUMMY_OVERTIME_WORKFLOW_ID } from '../../schemas/OvertimeApproval/constants';
import { isOvertimeApprovalBypassEnabled } from '../../../../../utils/overtimeApprovalBypass';
import { getWorkflowFirstSection, getWorkflowSectionKeys } from '../../../../../types';
import {
  extractUserAssignedSections,
  getSpoofUserHeader,
  isCurrentUserWorkflowCreator,
  isPermissionRelatedError,
  isUserAssignedToSection,
  normalizePermissionErrorMessage,
  toFormAssignedSectionKeys,
} from './utils';
import {
  isTestingDummyWorkflowId,
  shouldBypassWorkflowBackend,
} from '../../../../../utils/workflowTestingBypass';

// ============= Constants =============

const logger = createLogger('useFormSectionPermissions');

// ============= Hook =============

export const useFormSectionPermissions = ({
  sectionKey,
  workflowId,
  responsibleLawyer,
  lineManager,
}: UseFormSectionPermissionsParams): UseFormSectionPermissionsReturn => {
  const navigate = useNavigate();
  const { formState, updateFormData } = useFormContext();
  const { getAccessToken, spoofedUser, currentUser } = UseAuth();

  const [isDisabledByPermissions, setIsDisabledByPermissions] = useState(false);
  const [isDisabledByAssignment, setIsDisabledByAssignment] = useState(false);
  const [isDisabledByCreatorCheck, setIsDisabledByCreatorCheck] =
    useState(false);
  const [permissionsErrorMessage, setPermissionsErrorMessage] = useState<
    string | null
  >(null);

  const createdByName = formState.data?.createdByName;
  const createdBy = formState.data?.createdBy;
  const workflowType = formState.workflowType;

  const applyResponsibleLawyerBypass = useCallback((): boolean => {
    return isUserResponsibleLawyer(
      responsibleLawyer as unknown[],
      currentUser,
      getSpoofUserHeader(spoofedUser)
    );
  }, [currentUser, responsibleLawyer, spoofedUser]);

  const applyManagerBypass = useCallback((): boolean => {
    return isManager(
      lineManager,
      currentUser,
      getSpoofUserHeader(spoofedUser)
    );
  }, [currentUser, lineManager, spoofedUser]);

  const grantAssignedSections = useCallback(
    (assignedSections: string[]): string[] => {
      const normalized = toFormAssignedSectionKeys(assignedSections);
      const workflowSections = getWorkflowSectionKeys(workflowType);
      const canEditAny = canEditAnyWorkflow(currentUser);
      const isCreator = isCurrentUserWorkflowCreator(
        { createdByName, createdBy },
        currentUser
      );
      const canEditOwn = canEditOwnWorkflows(currentUser) && isCreator;
      const isOvertimeSection = isOvertimeApprovalSectionKey(sectionKey);

      let sectionsToStore = normalized;

      if (canEditAny) {
        sectionsToStore = Array.from(
          new Set([...normalized, ...workflowSections])
        );
      } else if (isOvertimeApprovalBypassEnabled && isOvertimeSection) {
        sectionsToStore = withTemporaryOvertimeAssignedSections(normalized);
      } else if (canEditOwn && sectionKey === getWorkflowFirstSection(workflowType)) {
        sectionsToStore = Array.from(new Set([...normalized, sectionKey]));
      }

      updateFormData({
        userAssignedSections: sectionsToStore,
      });

      return sectionsToStore;
    },
    [createdBy, createdByName, currentUser, sectionKey, updateFormData, workflowType]
  );

  const applySectionAccess = useCallback(
    (assignedSections: string[]): void => {
      const storedSections = grantAssignedSections(assignedSections);
      const isAssigned = isUserAssignedToSection(storedSections, sectionKey);
      const isResponsibleLawyer = applyResponsibleLawyerBypass();
      const isLineManager = applyManagerBypass();
      const canEditAny = canEditAnyWorkflow(currentUser);
      const isCreator = isCurrentUserWorkflowCreator(
        { createdByName, createdBy },
        currentUser
      );
      const canEditOwnFirstSection =
        canEditOwnWorkflows(currentUser) &&
        isCreator &&
        sectionKey === getWorkflowFirstSection(workflowType);

      setIsDisabledByPermissions(false);
      setIsDisabledByCreatorCheck(false);

      if (
        isAssigned ||
        isResponsibleLawyer ||
        isLineManager ||
        canEditAny ||
        canEditOwnFirstSection
      ) {
        setIsDisabledByAssignment(false);
        setPermissionsErrorMessage(null);
        return;
      }

      setIsDisabledByAssignment(true);
      setPermissionsErrorMessage(UNAUTHORISED_SECTION_MESSAGE);
    },
    [
      applyManagerBypass,
      applyResponsibleLawyerBypass,
      createdBy,
      createdByName,
      currentUser,
      grantAssignedSections,
      sectionKey,
      workflowType,
    ]
  );

  const handlePermissionDenied = useCallback(
    (errorMessage: string): void => {
      if (canEditAnyWorkflow(currentUser)) {
        applySectionAccess([]);
        logger.debug(
          'Edit any workflow permission - not disabling by permissions',
          {
            section: sectionKey,
            error: errorMessage,
          }
        );
        return;
      }

      if (
        isOvertimeApprovalBypassEnabled &&
        isOvertimeApprovalSectionKey(sectionKey)
      ) {
        applySectionAccess([]);
        logger.debug(
          'Overtime permission bypass - not disabling by permissions',
          {
            section: sectionKey,
            error: errorMessage,
          }
        );
        return;
      }

      if (shouldBypassWorkflowBackend(workflowType)) {
        applySectionAccess(getWorkflowSectionKeys(workflowType));
        return;
      }

      if (applyResponsibleLawyerBypass()) {
        setIsDisabledByPermissions(false);
        setPermissionsErrorMessage(null);
        logger.debug(
          'User is responsible lawyer - not disabling by permissions',
          {
            section: sectionKey,
            error: errorMessage,
          }
        );
        return;
      }

      if (applyManagerBypass()) {
        setIsDisabledByPermissions(false);
        setPermissionsErrorMessage(null);
        logger.debug('User is line manager - not disabling by permissions', {
          section: sectionKey,
          error: errorMessage,
        });
        return;
      }

      setIsDisabledByPermissions(true);
      setPermissionsErrorMessage(
        normalizePermissionErrorMessage(
          errorMessage,
          DEFAULT_PERMISSION_DENIED_MESSAGE
        )
      );
      logger.warning(
        'Permission error from involvement permissions - form disabled',
        {
          section: sectionKey,
          error: errorMessage,
        }
      );
      navigate('/403');
    },
    [
      applyManagerBypass,
      applyResponsibleLawyerBypass,
      applySectionAccess,
      currentUser,
      navigate,
      sectionKey,
    ]
  );

  const fetchInvolvement = useCallback(async (): Promise<void> => {
    // Testing: registry `permissions: true` — grant all sections, no involvement API.
    if (shouldBypassWorkflowBackend(workflowType)) {
      applySectionAccess(getWorkflowSectionKeys(workflowType));
      return;
    }

    if (
      isOvertimeApprovalBypassEnabled &&
      String(workflowId ?? '') === DUMMY_OVERTIME_WORKFLOW_ID
    ) {
      applySectionAccess([]);
      return;
    }

    if (isTestingDummyWorkflowId(workflowId)) {
      applySectionAccess(getWorkflowSectionKeys(workflowType));
      return;
    }

    try {
      const response = await involvementPermissions({
        workflowId: workflowId as number,
        getAccessToken,
        spoofedUser: getSpoofUserHeader(spoofedUser),
      });

      if (response.success && response.data) {
        logger.debug('Involvement Permissions', { data: response.data });
        const apiAssignedSections = extractUserAssignedSections(
          response.data,
          currentUser
        );
        applySectionAccess(apiAssignedSections);
        return;
      }

      if (response.error && isPermissionRelatedError(response.error)) {
        handlePermissionDenied(response.error);
        return;
      }

      if (canEditAnyWorkflow(currentUser)) {
        applySectionAccess([]);
        return;
      }

      if (
        isOvertimeApprovalBypassEnabled &&
        isOvertimeApprovalSectionKey(sectionKey)
      ) {
        applySectionAccess([]);
        return;
      }

      setIsDisabledByPermissions(false);
      logger.error('Failed to fetch Involvement Permissions', {
        error: response.error,
        section: sectionKey,
      });
    } catch (err: unknown) {
      logger.error('Error fetching Involvement Permissions', { error: err });

      if (err instanceof Error && isPermissionRelatedError(err.message)) {
        handlePermissionDenied(err.message);
        return;
      }

      if (canEditAnyWorkflow(currentUser)) {
        applySectionAccess([]);
        return;
      }

      if (
        isOvertimeApprovalBypassEnabled &&
        isOvertimeApprovalSectionKey(sectionKey)
      ) {
        applySectionAccess([]);
        return;
      }

      setIsDisabledByPermissions(false);
      setPermissionsErrorMessage(null);
    }
  }, [
    applySectionAccess,
    currentUser,
    getAccessToken,
    handlePermissionDenied,
    sectionKey,
    spoofedUser,
    workflowId,
    workflowType,
  ]);

  return {
    isDisabledByPermissions,
    isDisabledByAssignment,
    isDisabledByCreatorCheck,
    permissionsErrorMessage,
    fetchInvolvement,
  };
};
